import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Plus, RefreshCw, ChevronDown, Folder, Crop, Gem, Image as ImageIcon, Layers } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { classifyRef, useRefAnalysis, RefWarnings, AutoTag } from "@/components/RefTools";

const RATIOS = ["1:1", "4:3", "16:9", "9:16"];
const REF_TYPES = ["character", "style", "environment", "prop", "pose", "camera", "lighting", "color_palette"];

export default function ImageGenModal({ open, onOpenChange, onDone }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [prompt, setPrompt] = useState("");
  const [refs, setRefs] = useState([]);
  const [ratio, setRatio] = useState("4:3");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isAdmin = user?.role === "admin";
  const total = 15 * count;
  const warnings = useRefAnalysis(refs);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, 6 - refs.length)) {
      try {
        const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
        const { data } = await api.post(`/uploads/image`, { image_base64: b64, origin_url: window.location.origin });
        setRefs((r) => [...r, { url: data.url, type: "character", auto: true }]);
        classifyRef(data.url).then(({ type, label }) => {
          setRefs((r) => r.map((v) => v.url === data.url ? { ...v, type, label, auto: true } : v));
        });
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Enter a description first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/images/generate`, {
        prompt, aspect_ratio: ratio, count, references: refs,
      });
      onOpenChange(false);
      setPrompt(""); setRefs([]);
      await refreshUser();
      onDone && onDone(data.images);
      toast.success(`${data.images.length} image(s) generated ✨`);
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Not enough credits."); navigate("/pricing"); }
      else toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-zinc-800 text-[#F0E9DD] sm:max-w-md max-h-[94vh] overflow-y-auto p-0 gap-0 [&>button]:hidden">
        <DialogDescription className="sr-only">Generate an AI image from a description.</DialogDescription>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <button onClick={() => onOpenChange(false)} data-testid="image-modal-close" className="text-gold hover:opacity-70"><X size={24} /></button>
          <div className="flex items-center gap-1.5">
            <DialogTitle className="font-heading font-bold text-xl text-white">Image Generation</DialogTitle>
            <ChevronDown size={20} className="text-gold" />
          </div>
          <button onClick={() => navigate("/creations")} className="text-gold hover:opacity-70"><Folder size={22} /></button>
        </div>

        <div className="px-5 space-y-4">
          {/* reference images */}
          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-3">
              <span className="text-zinc-300 font-heading font-semibold">Reference Image <span className="text-zinc-600 font-normal">(Optional)</span></span>
              <div className="flex items-center gap-3">
                <button onClick={() => setRefs([])} data-testid="img-clear-refs" className="text-zinc-500 hover:text-gold"><RefreshCw size={15} /></button>
                <span className="w-px h-4 bg-zinc-700" />
                <span className="text-xs font-mono border border-zinc-700 rounded-full px-2.5 py-1 text-gold">{refs.length}/6</span>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="img-ref-input" />
            <div className="flex flex-wrap gap-2">
              {refs.map((r, i) => (
                <div key={i} className="w-16">
                  <div className="relative w-16 h-16 border border-zinc-800 rounded-md overflow-hidden">
                    <img src={r.url} alt="ref" className="w-full h-full object-cover" />
                    <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-ink-950/80 p-0.5"><X size={12} /></button>
                  </div>
                  <select value={r.type} onChange={(e) => setRefs((x) => x.map((v, j) => j === i ? { ...v, type: e.target.value, auto: false } : v))}
                    data-testid={`img-ref-type-${i}`} className="mt-1 w-16 bg-ink-950 border border-zinc-800 text-[9px] text-zinc-300 rounded px-0.5 py-0.5 focus:outline-none">
                    {REF_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                  </select>
                  {r.auto && <div className="mt-0.5"><AutoTag /></div>}
                </div>
              ))}
              {refs.length < 6 && (
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="img-add-ref" className="w-16 h-16 border border-dashed border-zinc-600 rounded-md flex items-center justify-center text-zinc-400 hover:border-gold hover:text-gold transition-colors">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={22} />}
                </button>
              )}
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">References are auto-classified by AI — override the tag anytime. Character identity is locked and never altered by style, environment or props.</p>
            <div className="mt-2"><RefWarnings warnings={warnings} /></div>
          </div>

          {/* description */}
          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 min-h-[200px] flex flex-col">
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="image-prompt"
              placeholder="Enter natural, coherent descriptions to unleash the power of image generation."
              className="w-full flex-1 bg-transparent text-base focus:outline-none resize-none placeholder:text-zinc-600 leading-relaxed" />
          </div>

          {/* params */}
          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 flex items-center justify-between text-sm">
            <div className="flex flex-col gap-2 flex-1">
              <span className="label-mono">Ratio</span>
              <div className="flex gap-2">
                {RATIOS.map((r) => (
                  <button key={r} onClick={() => setRatio(r)} data-testid={`img-ratio-${r}`}
                    className={`px-3 py-1.5 border rounded-md text-xs font-heading transition-colors ${ratio === r ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400"}`}>{r}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 pl-4 text-zinc-400">
              <span className="flex items-center gap-1 text-xs"><Gem size={14} className="text-gold" /> 1K</span>
              <span className="flex items-center gap-1 text-xs"><ImageIcon size={14} className="text-gold" /> Image 2 <span className="text-[9px] bg-gold text-ink-950 rounded px-1 font-bold">NEW</span></span>
            </div>
          </div>
        </div>

        {/* bottom */}
        <div className="px-5 pt-4 pb-1 flex items-center gap-3">
          <div className="flex items-center gap-3 border border-zinc-800 rounded-full px-3 py-2">
            <button onClick={() => setCount(Math.max(1, count - 1))} data-testid="img-count-minus" className="w-7 h-7 rounded-full border border-gold/60 text-gold flex items-center justify-center text-lg leading-none">−</button>
            <span className="flex items-center gap-1.5 text-base"><Layers size={16} className="text-gold" /> {count}</span>
            <button onClick={() => setCount(Math.min(4, count + 1))} data-testid="img-count-plus" className="w-7 h-7 rounded-full border border-gold/60 text-gold flex items-center justify-center"><Plus size={15} /></button>
          </div>
          <button onClick={generate} disabled={busy} data-testid="generate-image-submit"
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-2xl py-3 hover:brightness-105 transition disabled:opacity-60 shadow-[0_0_24px_rgba(198,138,91,0.35)]">
            {busy ? <Loader2 className="animate-spin" size={22} /> : <><Sparkle size={22} /> {isAdmin ? "Free" : total}</>}
          </button>
        </div>
        <p className="text-center text-xs text-zinc-600 pb-4 pt-2">AI-generated content. Illegal use prohibited.</p>
      </DialogContent>
    </Dialog>
  );
}
